import * as React from "react";

import throttle from "just-throttle";
import * as Redux from "react-redux";

import { getAptConfig } from "client/config";
import { CountryList } from "client/lib/apt-merchant";
import { RootState } from "client/store";

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

export type AddressComponents = Partial<{
  address: string;
  city: string;
  state: string;
  postalCode: string;
}>;

function getAddressFromComponents(
  geo: PlaceDetailsRes["address_components"],
  country?: CountryList
): AddressComponents {
  // for more info: https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
  const streetNumber = geo.find((g) =>
    g.types.find((t) => t === "street_number")
  )?.long_name;
  const streetName = geo.find((g) => g.types.find((t) => t === "route"))
    ?.long_name;
  const city = geo.find(
    (g) =>
      (g.types.find((t) => t === "locality") &&
        g.types.find((t) => t === "political")) ||
      g.types.find((t) => t === "postal_town")
  )?.long_name;

  const stateObj = geo.find(
    (g) =>
      g.types.find((t) => t === "administrative_area_level_1") &&
      g.types.find((t) => t === "political")
  );

  const state =
    country === "Canada" ? stateObj?.long_name : stateObj?.short_name;

  const postalCode = geo.find((g) => g.types.find((t) => t === "postal_code"))
    ?.long_name;
  const address = [streetNumber, streetName].filter(Boolean).join(" ");
  return {
    address,
    city,
    state,
    postalCode,
  };
}

const autocompleteService = { current: null };
const placeDetails = { current: null };

// https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletePrediction
// Note: prediction does not return postal_code
export type AutocompletePrediction = {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: [
      {
        offset: number;
        length: number;
      }
    ];
  };
  // theres more but we only use these values to get display a list of place predictions
};

// https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
// Note: this is not a full list but of only those we care about
export type PlaceDetailType =
  | "administrative_area_level_1" // State equivalent
  | "country"
  | "locality" // city elsewhere
  | "postal_town" // city in UK/EU
  | "political"
  | "postal_code"
  | "route"
  | "street_address"
  | "street_number";

//  https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceResult
export type PlaceDetailsRes = {
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: PlaceDetailType[];
  }>;
};

const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${getAptConfig(
  "GOOGLE_PLACES_KEY"
)}&libraries=places&callback=Function.prototype`;

type PlacePredictions = {
  addressComponents: AddressComponents | null;
  setAddressComponents: (v: AddressComponents | null) => void;
  placePredictions: (AutocompletePrediction | string)[];
  placeValue: AutocompletePrediction | null;
  setPlacePredictions: (v: (AutocompletePrediction | string)[]) => void;
  setPlaceValue: (v: AutocompletePrediction | null) => void;
};

export function usePlacePredictions(
  inputSearchTerm: string,
  preloadedAddressValue?: string
): PlacePredictions {
  const {
    merchant: {
      fieldValueMap: {
        companyInformation: { registeredCountry },
      },
    },
  } = Redux.useSelector(({ merchant }: RootState) => ({
    merchant,
  }));

  const [
    addressComponents,
    setAddressComponents,
  ] = React.useState<AddressComponents | null>(null);
  const [
    placeValue,
    setPlaceValue,
  ] = React.useState<AutocompletePrediction | null>(null);
  const [placePredictions, setPlacePredictions] = React.useState<
    (string | AutocompletePrediction)[]
  >([]);

  const loadedScript = React.useRef(false);

  React.useDebugValue("usePlacePredictions");

  if (typeof window !== "undefined" && !loadedScript.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(scriptUrl, document.querySelector("head"), "google-maps");
    }

    loadedScript.current = true;
  }

  const initAutocompleteService = () => {
    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }
  };
  const initPlacesService = () => {
    // https://developers.google.com/maps/documentation/javascript/reference/places-service
    if (!placeDetails.current && (window as any).google) {
      placeDetails.current = new (window as any).google.maps.places.PlacesService(
        document.createElement("div")
      );
    }
  };

  const fetchPlacePredictions = React.useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: AutocompletePrediction[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        200
      ),
    []
  );

  React.useEffect(() => {
    // fetch list of predictions on input change
    initAutocompleteService();

    if (!autocompleteService.current) {
      return;
    }

    if (inputSearchTerm === "") {
      setPlacePredictions(placeValue ? [placeValue] : []);
      return;
    }

    fetchPlacePredictions(
      { input: inputSearchTerm },
      (results?: AutocompletePrediction[]) => {
        let newOptions = [] as AutocompletePrediction[];

        if (placeValue) {
          newOptions = [placeValue];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }
        // append the input search term at the end of list for manual entry
        setPlacePredictions([...newOptions, inputSearchTerm]);
      }
    );
  }, [placeValue, inputSearchTerm, fetchPlacePredictions]);

  const fetchPlaceDetails = React.useMemo(
    () => (
      request: {
        placeId: string;
        fields?: string[];
      },
      callback: (res: PlaceDetailsRes, status: "OK" | string) => void
    ): void => {
      (placeDetails.current as any).getDetails(request, callback);
    },
    []
  );

  React.useEffect(() => {
    // fetch selected place details on selection of place
    initPlacesService();

    if (!autocompleteService.current || !placeDetails.current || !placeValue) {
      return;
    }

    fetchPlaceDetails(
      { placeId: placeValue.place_id, fields: ["address_components"] },
      (placeDetailsRes: PlaceDetailsRes, status) => {
        if (status === "OK") {
          setAddressComponents(
            getAddressFromComponents(
              placeDetailsRes?.address_components,
              registeredCountry
            )
          );
        }
      }
    );
  }, [placeValue, inputSearchTerm, registeredCountry, fetchPlaceDetails]);

  React.useEffect(() => {
    // auto populate prediction from address in merchant model if field has value
    if (!placeValue) {
      if (preloadedAddressValue && placePredictions.length) {
        const found = placePredictions.find(
          (p) =>
            typeof p !== "string" &&
            p?.structured_formatting?.main_text === preloadedAddressValue
        );
        if (found && typeof found !== "string") {
          setPlaceValue(found);
        }
      }
    }
  }, [preloadedAddressValue, placeValue, placePredictions, registeredCountry]);

  return {
    addressComponents,
    placeValue,
    placePredictions,
    setAddressComponents,
    setPlaceValue,
    setPlacePredictions,
  };
}
